import { useEffect, useRef } from "react";
import {
  Spinner,
  Text,
  Toast,
  ToastTitle,
  Tooltip,
  mergeClasses,
  useToastController,
} from "@fluentui/react-components";
import { ArrowClockwise16Regular, Info16Regular } from "@fluentui/react-icons";
import { useSelector } from "react-redux";
import { RootState } from "store";
import { Constants } from "common/constants";
import { TOASTER_ID } from "components/toast/toast.constants";
import { useAiMetrics } from "../../context/AiMetricsContext";
import { useStyles } from "./FiltersBar.styles";
import { RefreshControl } from "../RefreshControl/RefreshControl";
import { FilterDatePopover } from "./FilterDatePopover";
import { FilterEntityPopover } from "./FilterEntityPopover";
import { IDateRange, IEntityOption } from "./FilterEntityPopover.types";
import { useAdoRefresh } from "../../hooks/useAdoRefresh";
import { formatFilterDate } from "../../utils/filterHelpers";

const ROLLING_TOOLTIP =
  "Date range is set automatically in Rolling 3-Month mode.";
const ALREADY_RUNNING_TOOLTIP = "A task is already running";
const ALREADY_RUNNING_TOAST = "A refresh is already in progress.";

export interface IEntityFilterConfig {
  label: string;
  options: IEntityOption[];
  isLoading: boolean;
  isError: boolean;
  selected: string[];
  onChange: (names: string[]) => void;
  loadingText?: string;
  allLabel?: string;
  skipAutoSelectAll?: boolean;
  preserveOptionOrder?: boolean;
  dateRange?: IDateRange;
}

export interface IFiltersBarProps {
  entity: IEntityFilterConfig;
  sprint?: IEntityFilterConfig;
  maxDate?: Date;
}

interface IEntityFilterSectionProps {
  config: IEntityFilterConfig;
}

const EntityFilterSection = ({
  config,
}: IEntityFilterSectionProps): JSX.Element => {
  const styles = useStyles();

  const {
    label,
    options,
    isLoading,
    isError,
    selected,
    onChange,
    loadingText,
    allLabel,
    skipAutoSelectAll,
    preserveOptionOrder = false,
    dateRange,
  } = config;

  return (
    <div className={styles.entitySection}>
      <Text className={styles.label}>{label}</Text>
      <FilterEntityPopover
        label={label}
        options={options}
        isLoading={isLoading}
        isError={isError}
        selected={selected}
        onApply={onChange}
        loadingText={loadingText}
        allLabel={allLabel}
        skipAutoSelectAll={skipAutoSelectAll}
        preserveOptionOrder={preserveOptionOrder}
        dateRange={dateRange}
      />
    </div>
  );
};

const RefreshDataButton = (): JSX.Element | null => {
  const styles = useStyles();
  const { dispatchToast } = useToastController(TOASTER_ID);

  const currentUser = useSelector(
    (state: RootState) => state.currentUserSlice.value,
  );
  const isAdmin = !!currentUser?.roles?.includes(
    Constants.AI_METRICS_ADMINS_GROUP_NAME_VALUE,
  );

  const { triggerRefresh, isRefreshing, refreshPhase, lastError } =
    useAdoRefresh();

  const handleRefreshClick = (): void => {
    if (isRefreshing) return;
    triggerRefresh({
      onConflict: () =>
        dispatchToast(
          <Toast className={styles.wideToast}>
            <ToastTitle>{ALREADY_RUNNING_TOAST}</ToastTitle>
          </Toast>,
          { intent: "info" },
        ),
    });
  };

  const handledPhase = useRef<string | null>(null);
  useEffect(() => {
    if (handledPhase.current === refreshPhase) return;
    handledPhase.current = refreshPhase;

    if (refreshPhase === "success") {
      dispatchToast(
        <Toast className={styles.wideToast}>
          <ToastTitle>Azure DevOps metrics updated successfully.</ToastTitle>
        </Toast>,
        { intent: "success" },
      );
    } else if (refreshPhase === "discarded") {
      dispatchToast(
        <Toast className={styles.wideToast}>
          <ToastTitle>{lastError ?? "Refresh was discarded."}</ToastTitle>
        </Toast>,
        { intent: "warning" },
      );
    } else if (refreshPhase === "failure") {
      dispatchToast(
        <Toast className={styles.wideToast}>
          <ToastTitle>
            {lastError ?? "The refresh failed. Please try again."}
          </ToastTitle>
        </Toast>,
        { intent: "error" },
      );
    }
  }, [refreshPhase, lastError, dispatchToast]);

  if (!isAdmin) return null;

  return (
    <div className={styles.adminControls}>
      <Tooltip
        content={ALREADY_RUNNING_TOOLTIP}
        relationship="label"
        positioning="above"
        visible={isRefreshing ? undefined : false}
      >
        <button
          type="button"
          className={styles.refreshButton}
          aria-disabled={isRefreshing}
          onClick={handleRefreshClick}
          aria-label={isRefreshing ? "Refresh in progress" : "Refresh ADO data"}
          aria-busy={isRefreshing}
        >
          {isRefreshing ? (
            <Spinner size="tiny" aria-hidden="true" />
          ) : (
            <ArrowClockwise16Regular aria-hidden="true" />
          )}
          Refresh data
        </button>
      </Tooltip>
    </div>
  );
};

export const FiltersBar = ({
  entity,
  sprint,
  maxDate,
}: IFiltersBarProps): JSX.Element => {
  const styles = useStyles();

  const {
    startDate,
    endDate,
    viewMode,
    pickersReadOnly,
    comparisonLabel,
    setStartDate,
    setEndDate,
    setViewMode,
  } = useAiMetrics();

  const dataLagMessage = maxDate
    ? `Data isn't available for the last few days yet, so dates up to ${formatFilterDate(maxDate)} can be selected.`
    : undefined;

  return (
    <>
      <div className={styles.bar}>
        <div className={styles.filterRow}>
          <div className={styles.firstRowfilterBarWrapper}>
            <div
              className={styles.segmentedGroup}
              role="group"
              aria-label="Comparison view mode"
            >
              <button
                type="button"
                className={mergeClasses(
                  styles.segmentedBtn,
                  styles.segmentedBtnLeft,
                  viewMode === "manual" ? styles.segmentedBtnActive : undefined,
                )}
                aria-pressed={viewMode === "manual"}
                onClick={() => setViewMode("manual")}
              >
                Date Range
              </button>
              <button
                type="button"
                className={mergeClasses(
                  styles.segmentedBtn,
                  viewMode === "rolling"
                    ? styles.segmentedBtnActive
                    : undefined,
                )}
                aria-pressed={viewMode === "rolling"}
                onClick={() => setViewMode("rolling")}
              >
                Rolling 3-Month
              </button>
              <button
                type="button"
                className={mergeClasses(
                  styles.segmentedBtn,
                  styles.segmentedBtnRight,
                  viewMode === "yoy" ? styles.segmentedBtnActive : undefined,
                )}
                aria-pressed={viewMode === "yoy"}
                onClick={() => setViewMode("yoy")}
              >
                Year-over-Year ({endDate.getFullYear() - 1} vs{" "}
                {endDate.getFullYear()})
              </button>
            </div>

            <div className={styles.entityGroup}>
              {sprint && <EntityFilterSection config={sprint} />}
              <EntityFilterSection config={entity} />
            </div>
          </div>

          <RefreshDataButton />
        </div>
        <div className={styles.filterRow}>
          <div
            className={styles.dateSection}
            title={pickersReadOnly ? ROLLING_TOOLTIP : undefined}
          >
            <div className={styles.secondRowFilterBarWrapper}>
              <Text className={styles.label}>Date Range</Text>
              <FilterDatePopover
                value={startDate}
                ariaLabel="Start date"
                maxDate={maxDate}
                disabled={pickersReadOnly}
                onApply={setStartDate}
              />
              <Text className={styles.separator}>to</Text>
              <FilterDatePopover
                value={endDate}
                ariaLabel="End date"
                minDate={startDate}
                maxDate={maxDate}
                disabled={pickersReadOnly}
                onApply={setEndDate}
              />
              {viewMode !== "manual" ? (
                <div className={styles.viewModeGroup}>
                  <span className={styles.comparisonChip}>
                    {comparisonLabel}
                  </span>
                </div>
              ) : (
                <></>
              )}
            </div>
            {dataLagMessage && (
              <Tooltip
                content={dataLagMessage}
                relationship="description"
                positioning="above"
              >
                <button
                  type="button"
                  className={styles.infoIconButton}
                  aria-label="Data availability information"
                >
                  <Info16Regular aria-hidden="true" />
                </button>
              </Tooltip>
            )}

            <RefreshControl />
          </div>
        </div>
      </div>
    </>
  );
};
